import clsx from 'clsx';
import { memo, type ReactNode } from 'react';

import { Layout } from '../Layout/Layout';
import {
  Connector,
  type ConnectorStatus,
} from './subcomponents/Connector/Connector';
import { Step, type StepProps } from './subcomponents/Step/Step';
import './Steps.css';

export type StepsItem = Omit<StepProps, 'direction'> & {
  /** Content rendered beneath this step. Vertical direction only. */
  content?: ReactNode;
};

export type StepsProps = {
  /** Accessible name for the sequence (localized by the consumer). */
  label: string;
  /** Ordered steps in the sequence. */
  steps: StepsItem[];
  /** Orientation of the sequence. @default 'horizontal' */
  direction?: 'horizontal' | 'vertical';
};

function resolveConnectorStatus(
  previousStatus: StepProps['status'],
  nextStatus: StepProps['status'],
): ConnectorStatus {
  if (nextStatus === 'current') {
    return 'current';
  }
  if (previousStatus === 'completed') {
    return 'completed';
  }
  return 'future';
}

export const Steps = memo(function Steps({
  label,
  steps,
  direction = 'horizontal',
}: StepsProps) {
  const isVertical = direction === 'vertical';

  return (
    <Layout
      aria-label={label}
      as="ol"
      className={clsx(
        'eds-steps',
        isVertical && 'eds-steps--direction-vertical',
      )}
      direction={isVertical ? 'column' : 'row'}
      spacing="none"
    >
      {steps.map((step, index) => (
        <li
          key={`${step.text}-${index}`}
          aria-current={
            !step.onClick && step.status === 'current' ? 'step' : undefined
          }
          className="eds-steps--item"
        >
          {/* Horizontal: connector leads the later item — every item shares
              the same flex:1 width, so ±50% of its own box always reaches
              the previous indicator. Vertical: connector trails the earlier
              item instead and reaches forward past its own bottom edge by a
              fixed offset (see Steps.css) — items don't share a height, but
              that offset is constant regardless of either item's height. */}
          {!isVertical && index > 0 && (
            <span className="eds-steps--connector">
              <Connector
                direction={direction}
                status={resolveConnectorStatus(
                  steps[index - 1].status,
                  step.status,
                )}
              />
            </span>
          )}
          <Step
            direction={direction}
            secondaryText={step.secondaryText}
            status={step.status}
            statusLabel={step.statusLabel}
            text={step.text}
            onClick={step.onClick}
          />
          {isVertical && step.content && (
            <div className="eds-steps--item-content">{step.content}</div>
          )}
          {isVertical && index < steps.length - 1 && (
            <span className="eds-steps--connector">
              <Connector
                direction={direction}
                status={resolveConnectorStatus(
                  step.status,
                  steps[index + 1].status,
                )}
              />
            </span>
          )}
        </li>
      ))}
    </Layout>
  );
});
